import { useMemo } from 'react';
import { useGetEventByIdQuery, useGetEventsQuery, useGetEventRecordingQuery, useGetOnboardingActivationStatusQuery } from '../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/LearningEventUserRpc.hooks';
import { GET_EVENT_BY_ID_QUERY, GET_EVENTS_QUERY } from '../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/LearningEventUserRpc.dfc.new';
import { useRegisterUserToEventSessionMutation } from '../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/EventRegistrationRpc.hooks';
import { useGetProfessorsQuery } from '../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/ProfessorRpc.hooks';
import { useGetSeriesEventsByLanguageQuery } from '../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/LearningEventSeriesRpc.hooks';
import { MY_LEARNING_QUERY_FIELD_NAME } from '../myLearning/myLearningQueries';
export const useLearningEvent = eventId => {
  var _data$sessions;
  const {
    data,
    loading,
    error
  } = useGetEventByIdQuery({
    variables: {
      eventId
    },
    skip: !eventId
  });
  const sessions = ((_data$sessions = data === null || data === void 0 ? void 0 : data.sessions) !== null && _data$sessions !== void 0 ? _data$sessions : []).filter(s => !s.deletedAt && s.startTimeMs !== undefined);
  return {
    learningEvent: data,
    sessions,
    loading,
    error
  };
};
export const useEventRecording = eventId => {
  var _data$recordingUrls, _data$attended;
  const {
    data,
    loading,
    error
  } = useGetEventRecordingQuery({
    variables: {
      eventId
    },
    skip: !eventId
  });
  return {
    recordingUrls: (_data$recordingUrls = data === null || data === void 0 ? void 0 : data.recordingUrls) !== null && _data$recordingUrls !== void 0 ? _data$recordingUrls : [],
    attended: (_data$attended = data === null || data === void 0 ? void 0 : data.attended) !== null && _data$attended !== void 0 ? _data$attended : false,
    loading,
    error
  };
};
export const useLearningEvents = (variables = {}, {
  skip
} = {}) => {
  var _data$results, _data$hasMore;
  const {
    data,
    loading,
    error,
    refetch
  } = useGetEventsQuery({
    variables,
    skip
  });
  return {
    learningEvents: (_data$results = data === null || data === void 0 ? void 0 : data.results) !== null && _data$results !== void 0 ? _data$results : [],
    learningEventsHasMore: (_data$hasMore = data === null || data === void 0 ? void 0 : data.hasMore) !== null && _data$hasMore !== void 0 ? _data$hasMore : false,
    learningEventsNextOffset: data === null || data === void 0 ? void 0 : data.offset,
    learningEventsLoading: loading,
    learningEventsError: error,
    refetchLearningEvents: refetch
  };
};
export const useOnboardingActivationStatus = ({
  skip = false
} = {}) => {
  var _data$withinActivatio;
  const {
    data,
    loading,
    error
  } = useGetOnboardingActivationStatusQuery({
    skip
  });
  return {
    isWithinActivationWindow: (_data$withinActivatio = data === null || data === void 0 ? void 0 : data.withinActivationWindow) !== null && _data$withinActivatio !== void 0 ? _data$withinActivatio : false,
    loading,
    error
  };
};
export const useEventInstructors = professorIds => {
  const {
    data,
    loading,
    error
  } = useGetProfessorsQuery({
    variables: {
      professorIds
    },
    skip: professorIds.length === 0
  });
  const idsKey = professorIds.join(',');
  const professors = useMemo(() => {
    var _data$professors;
    const professorsById = (_data$professors = data === null || data === void 0 ? void 0 : data.professors) !== null && _data$professors !== void 0 ? _data$professors : {};
    return professorIds.map(id => professorsById[String(id)]).filter(p => p !== undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, idsKey]);
  return {
    professors,
    loading,
    error
  };
};
export const useSeriesEventsByLanguage = (language, {
  skip
} = {}) => {
  const {
    data,
    loading,
    error
  } = useGetSeriesEventsByLanguageQuery({
    variables: {
      language
    },
    skip
  });
  const orderedEventIds = useMemo(() => {
    var _data$events;
    return [...((_data$events = data === null || data === void 0 ? void 0 : data.events) !== null && _data$events !== void 0 ? _data$events : [])]
    // The series returns its full membership including inactive events, but
    // `getEvents` filters those out server-side. Dropping them here keeps
    // `orderedEventIds` consistent with what the events query will return,
    // so an all-inactive series reads as empty and callers can fall back.
    .filter(seriesEvent => seriesEvent.event.active).sort((a, b) => a.position - b.position).map(seriesEvent => seriesEvent.event.id);
  }, [data]);
  return {
    orderedEventIds,
    seriesId: data === null || data === void 0 ? void 0 : data.seriesId,
    seriesLoading: loading,
    seriesError: error
  };
};
export const useRegisterForSession = ({
  eventId,
  onCompleted,
  onError,
  onFallback
}) => {
  const [mutate, {
    loading,
    error
  }] = useRegisterUserToEventSessionMutation({
    onCompleted: wrapped => {
      if (wrapped.result.registrationSucceeded === true) {
        onCompleted === null || onCompleted === void 0 || onCompleted(wrapped.result);
      } else {
        onFallback === null || onFallback === void 0 || onFallback(wrapped.result.registrationUrl);
      }
    },
    onError,
    // A successful registration adds a live event to My Learning; evict its
    // cache so the list refetches. Skip the Zoom-URL fallback path (no reg).
    update: (cache, result) => {
      var _result$data;
      if ((_result$data = result.data) !== null && _result$data !== void 0 && (_result$data = _result$data.result) !== null && _result$data !== void 0 && _result$data.registrationSucceeded) {
        cache.evict({
          fieldName: MY_LEARNING_QUERY_FIELD_NAME
        });
      }
    },
    refetchQueries: [{
      query: GET_EVENT_BY_ID_QUERY,
      variables: {
        eventId
      }
    }, GET_EVENTS_QUERY],
    awaitRefetchQueries: true
  });
  return {
    registerForSession: eventSessionExternalId => mutate({
      variables: {
        eventSessionExternalId
      }
    }),
    registrationLoading: loading,
    registrationError: error
  };
};